/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


export type MenuItem = { label: string; action: string; variant?: string };

function itemLabel(item: string | MenuItem): string {
  return typeof item === "string" ? item : item.label;
}

function itemAction(item: string | MenuItem): string {
  return typeof item === "string" ? item.toLowerCase() : item.action;
}

function itemVariant(item: string | MenuItem): string {
  return typeof item === "string" ? "" : item.variant ?? "";
}

const styles = css`
  :host {
    position: fixed;
    z-index: 300;
  }

  .menu {
    background: var(--popover, #ffffff);
    border: 1px solid var(--border, #e4e4e7);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
    box-sizing: border-box;
    color: var(--popover-foreground, #18181b);
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 176px;
    padding: 4px;
  }

  .item {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--popover-foreground, #18181b);
    cursor: pointer;
    display: flex;
    font: inherit;
    font-size: 0.875rem;
    gap: 8px;
    height: 32px;
    outline: none;
    padding: 0 8px;
    text-align: left;
    transition:
      background-color 120ms ease,
      color 120ms ease;
    width: 100%;
  }

  .item:hover,
  .item:focus-visible {
    background: var(--accent, #f4f4f5);
    color: var(--accent-foreground, #18181b);
  }

  .item.destructive {
    color: var(--destructive, #dc2626);
  }

  .item.destructive:hover,
  .item.destructive:focus-visible {
    background: var(--destructive-muted, rgb(220 38 38 / 10%));
    color: var(--destructive, #dc2626);
  }
`;

export class AppDropdownMenu extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppDropdownMenu)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["open","data-open","items","data-items","position","data-position"];

  #open = false;
  #items = [];
  #position = "";
  #openSet = false;
  #itemsSet = false;
  #positionSet = false;
  #hooksLive = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get open() {
    return this.#open;
  }

  set open(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#open, next)) return;
    this.#open = next;
    this.#openSet = true;
    this.#updateBindings('open');
  }

  get items() {
    return this.#items;
  }

  set items(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#items, next)) return;
    this.#items = next;
    this.#itemsSet = true;
    this.#updateBindings('items');
  }

  get position() {
    return this.#position;
  }

  set position(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#position, next)) return;
    this.#position = next;
    this.#positionSet = true;
    this.#updateBindings('position');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-open')) this.open = this.dataset.open;
    if (this.hasAttribute('data-items')) this.items = this.dataset.items;
    if (this.hasAttribute('data-position')) this.position = this.dataset.position;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.open = props.open;
      this.items = props.items;
      this.position = props.position;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#openSet) this.open = this.dataset.open;
    if (!this.#itemsSet) this.items = this.dataset.items;
    if (!this.#positionSet) this.position = this.dataset.position;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    this.#effect0();
    this.#effect1();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
    if (this.#effect1Cleanup) { this.#effect1Cleanup(); this.#effect1Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'open':
      case 'data-open':
        this.open = newValue;
        break;
      case 'items':
      case 'data-items':
        this.items = newValue;
        break;
      case 'position':
      case 'data-position':
        this.position = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="menu" role="menu" style="display:none" data-argon="2955"></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    if (this.#position)
        this.setAttribute("style", this.#position);
    this.style.pointerEvents = this.#open ? "" : "none";
    this.style.visibility = this.#open ? "" : "hidden";
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }

  #effect1Cleanup;
  #effect1() {
    if (this.#effect1Cleanup) this.#effect1Cleanup();
    const cleanup = (() => {
    const menu = this.shadowRoot?.querySelector<HTMLDivElement>(".menu");
    if (!menu)
        return;
    menu.replaceChildren();
    for (const item of this.#items as Array<string | MenuItem>) {
        const button = document.createElement("button");
        button.type = "button";
        button.role = "menuitem";
        button.className = `item ${itemVariant(item)}`.trim();
        button.dataset.menuItem = "";
        button.dataset.action = itemAction(item);
        button.textContent = itemLabel(item);
        menu.appendChild(button);
    }
    menu.style.display = this.#open ? "" : "none";
})();
    this.#effect1Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    

    if (this.#hooksLive && (prop === 'position' || prop === 'open')) this.#effect0();
    if (this.#hooksLive && (prop === 'items' || prop === 'open')) this.#effect1();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2955').forEach(el => this.#listen(el, '2955', 'click', (event: Event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-menu-item]");
    const action = button?.dataset.action;
    if (!action)
        return;
    this.dispatchEvent(new CustomEvent("select", { bubbles: true, composed: true, detail: { action } }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
